/**
 * Flatten sanitized richtext HTML so it can be injected INSIDE an `<h1>`–`<h6>` element.
 *
 * WHY THIS EXISTS (founder, 16/09/2026 — « le heading est grand dans le builder, tout petit dans le
 * preview »). Every heading field is a Tiptap richtext field whose toolbar offers H2 / H3. A seller who
 * clicks H2 stores `<h2>Cousu, pas imprimé.</h2>`, and the heading block injects that inside its OWN
 * `<h2 class="aqx-hd-…">`. The builder and the shop then disagree, because two DIFFERENT parsers read it:
 *
 *  - BUILDER — React sets it through `dangerouslySetInnerHTML`, i.e. the FRAGMENT parser with the `<h2>`
 *    as context element. The context element is not on the stack of open elements, so the "pop the current
 *    h1–h6" rule never fires: the inner `<h2>` stays a CHILD and inherits the block's 39px. Looks right.
 *  - STOREFRONT — the same markup arrives as SSR text and goes through the DOCUMENT parser, where
 *    « a start tag h1…h6: if the current node is an h1–h6 element, pop it » applies. The outer
 *    `<h2 class="aqx-hd-…">` is closed EMPTY and the seller's `<h2>` is re-parented as its SIBLING — with
 *    no class, so the block's font-size rule misses it and it falls back to the 16px body size.
 *
 * Measured on the running stack: builder 39px, storefront 16px, same document. The heading block already
 * owns the size (the « Taille du titre » tier), so the richtext inside it must be PHRASING content only.
 * We therefore unwrap the block-level wrappers (h1–h6, p, div), keeping their content and turning a real
 * multi-block value into `<br>`-separated lines. `<strong>` / `<em>` / `<a>` / `<br>` — everything the
 * Highlight subsystems target — pass through untouched, so nothing that already rendered moves.
 *
 * Runs AFTER `sanitizeRichtextHtml`: this is a layout normalizer, never the XSS barrier.
 */
/** Closing tag of a block-level wrapper → a line boundary. */
const CLOSE_BLOCK = /<\/(?:h[1-6]|p|div)\s*>/gi;
/** Opening tag of a block-level wrapper (with or without attributes) → dropped. */
const OPEN_BLOCK = /<(?:h[1-6]|p|div)(?:\s[^>]*)?>/gi;
/** Two or more line boundaries in a row (nested wrappers, blank paragraphs) → one. */
const REPEATED_BREAK = /(?:<br\s*\/?>\s*){2,}/gi;
/** The editor's last `</p>` is not a line the seller typed; neither is a leading one. */
const EDGE_BREAK = /^(?:\s*<br\s*\/?>)+|(?:<br\s*\/?>\s*)+$/gi;
export function inlineHeadingHtml(html) {
    if (typeof html !== 'string' || html.length === 0)
        return '';
    return html
        .replace(CLOSE_BLOCK, '<br>')
        .replace(OPEN_BLOCK, '')
        .replace(REPEATED_BREAK, '<br>')
        .replace(EDGE_BREAK, '')
        .trim();
}
//# sourceMappingURL=inlineHeadingHtml.js.map